import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet } from '@/lib/api-client';
import { creditBalanceQueryKey } from '@/hooks/use-credit-balance';
import { isOpenTask, type PublicToolTask, type ToolTasksResponse } from '@/lib/ai-tools/types';

export const toolTasksQueryKey = (slug: string) => ['ai-tool-tasks', slug] as const;

/**
 * The signed-in user's renders + credit balance for one tool. Polls every
 * 5s while a render is open (the server throttles provider checks itself).
 */
export function useToolTasks(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: toolTasksQueryKey(slug),
    queryFn: () => apiGet<ToolTasksResponse>(`/api/ai-tools/${slug}/tasks`),
    enabled,
    refetchInterval: (query) => (query.state.data?.tasks.some(isOpenTask) ? 5000 : false),
  });
}

/** Show a just-started render (and its charge) before the next poll lands. */
export function useAddStartedTask(slug: string) {
  const queryClient = useQueryClient();
  return (task: PublicToolTask) => {
    queryClient.setQueryData<ToolTasksResponse>(toolTasksQueryKey(slug), (current) =>
      current ? { balance: current.balance - task.credits, tasks: [task, ...current.tasks] } : current,
    );
    void queryClient.invalidateQueries({ queryKey: toolTasksQueryKey(slug) });
    void queryClient.invalidateQueries({ queryKey: creditBalanceQueryKey });
  };
}
